// src/Commoncontext/DocumentsContext.js
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import DocumentsApi from "../Api/DocumentsApi";

const DocumentsContext = createContext(null);

const CACHE_KEY = "documents_cache_etag_v1";

function createCache() {
  return {
    map: new Map(), // companyId -> { documents, etag, fetchedAt }
    hydrate() {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) {
          const obj = JSON.parse(raw);
          this.map = new Map(Object.entries(obj));
        }
      } catch (e) {
        console.warn("Documents cache hydrate failed:", e);
      }
    },
    persist() {
      try {
        const obj = Object.fromEntries(this.map);
        localStorage.setItem(CACHE_KEY, JSON.stringify(obj));
      } catch (e) {
        console.warn("Documents cache persist failed:", e);
      }
    },
  };
}

export const DocumentsProvider = ({ children }) => {
  const cache = useRef(createCache()).current;

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cache.hydrate();
  }, [cache]);

  /**
   * Hakee dokumentit tietylle taloyhtiölle ETag-välimuistilla.
   */
  const fetchDocuments = useCallback(
    async (companyId) => {
      if (!companyId) return;
      const entry = cache.map.get(companyId);
      if (entry) {
        setDocuments(entry.documents || []);
      }

      setLoading(!entry);

      try {
        const resp = await DocumentsApi.fetchDocuments(companyId, {
          etag: entry?.etag,
        });

        if (resp.status === 304) {
          // ei muutoksia
          return;
        }

        const newEntry = {
          documents: resp.documents,
          etag: resp.etag,
          fetchedAt: Date.now(),
        };
        cache.map.set(companyId, newEntry);
        cache.persist();
        setDocuments(resp.documents);
      } catch (e) {
        console.error("fetchDocuments error:", e);
        if (!entry) setDocuments([]);
      } finally {
        setLoading(false);
      }
    },
    [cache]
  );

  /**
   * Lataa dokumentin ja lisää se listaan optimistisesti.
   */
  const uploadDocument = useCallback(
  async (companyId, file, metadata = {}) => {
    const saved = await DocumentsApi.uploadDocument(companyId, file, metadata);

    // AI-data tulee myöhemmin fetchDocumentsin kautta
    const enriched = { ...saved, ai: null };

    setDocuments((prev) => [enriched, ...(prev || [])]);

    const entry = cache.map.get(companyId);
    if (entry) {
      cache.map.set(companyId, {
        ...entry,
        etag: undefined,
        documents: [enriched, ...(entry.documents || [])],
      });
      cache.persist();
    }

    return enriched;
  },
  [cache]
);

  const value = {
    documents,
    loading,
    fetchDocuments,
    uploadDocument,
  };

  return (
    <DocumentsContext.Provider value={value}>
      {children}
    </DocumentsContext.Provider>
  );
};

export const useDocuments = () => useContext(DocumentsContext);
